import React, { useState } from 'react';
import { IoMdCheckmark } from 'react-icons/io';
import { FaTimes } from 'react-icons/fa';

import { useWorkflowExecutions } from '../../context/WorkflowExecutionsProvider';
import WorkflowExecutionWindow from '../../components/workflowExecution/WorkflowExecutionWindow';

const WorkflowExecutions = () => {
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const { workflowExecutions } = useWorkflowExecutions(); // Get executions from context.
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedExecution, setSelectedExecution] = useState(null);

  // Basic size settings for stacking cards
  const fullHeight = 100; // full card height
  const collapsedPeek = 20; // visible part when not hovered

  const computeTranslateY = (index) => {
    if (hoveredIndex === null) {
      return index * collapsedPeek;
    } else {
      if (index <= hoveredIndex) {
        return index * collapsedPeek;
      } else {
        const distance = index - hoveredIndex;
        let extraOffset = fullHeight;
        if (distance === 2) extraOffset += 10;
        else if (distance >= 3) extraOffset += 15;
        return (
          hoveredIndex * collapsedPeek +
          extraOffset +
          (distance - 1) * collapsedPeek
        );
      }
    }
  };

  const computeStyle = (index) => {
    const translateY = computeTranslateY(index);
    const distanceFromHovered =
      hoveredIndex === null ? 0 : Math.min(Math.abs(index - hoveredIndex), 3);
    let scale = 1,
      opacity = 1;
    if (hoveredIndex !== null) {
      if (distanceFromHovered === 0) {
        scale = 1.05;
        opacity = 1;
      } else if (distanceFromHovered === 1) {
        scale = 0.95;
        opacity = 0.9;
      } else if (distanceFromHovered >= 2) {
        scale = 0.9;
        opacity = 0.8;
      }
    }

    let transform = `translateY(${translateY}px) scale(${scale})`;
    if (hoveredIndex !== null && index === hoveredIndex) {
      transform += ' rotateX(3deg) rotateY(3deg) translateZ(10px)';
    } else {
      transform += ' translateZ(0)';
    }

    const zIndex =
      hoveredIndex === null
        ? workflowExecutions.length - index
        : index === hoveredIndex
          ? 100
          : 100 - Math.abs(index - hoveredIndex);

    return {
      transform,
      opacity,
      zIndex,
      height: `${fullHeight}px`,
      transformStyle: 'preserve-3d',
    };
  };

  const containerHeight =
    hoveredIndex === null
      ? workflowExecutions.length * collapsedPeek + fullHeight
      : (() => {
          const lastIndex = workflowExecutions.length - 1;
          return computeTranslateY(lastIndex) + fullHeight;
        })();

  // Open modal on execution card click
  const handleExecutionClick = (execution) => {
    setSelectedExecution(execution);
    setIsModalOpen(true);
  };

  return (
    <div className="pl-5 pr-5">
      <div
        className="relative"
        style={{ perspective: '1000px', height: containerHeight }}
      >
        {workflowExecutions.map((execution, index) => (
          // eslint-disable-next-line
          <div
            key={execution.execution_id}
            style={computeStyle(index)}
            className="absolute left-0 right-0 transition-all duration-300"
            // Only update hoveredIndex on mouse enter.
            onMouseEnter={() => setHoveredIndex(index)}
            onClick={() => handleExecutionClick(execution)}
            // Remove onMouseLeave to keep the last hovered card active.
            // onMouseLeave={() => setHoveredIndex(null)}
          >
            <ExecutionCard
              execution={execution}
              isHovered={hoveredIndex === index}
            />
          </div>
        ))}
      </div>
      {/* Modal for execution details */}
      {selectedExecution && (
        <WorkflowExecutionWindow
          workflowId={selectedExecution.workflow_id}
          currentExecutionId={selectedExecution.execution_id}
          isOpen={isModalOpen}
          closeModal={() => {
            setIsModalOpen(false);
            setSelectedExecution(null);
          }}
        />
      )}
    </div>
  );
};
const ExecutionCard = ({ execution, isHovered }) => {
  const { status, workflowTitle } = execution;
  const lowerStatus = status.toLowerCase();

  // Determine border color based on status
  const borderColors = {
    running: 'border-yellow-500',
    success: 'border-green-500',
    failed: 'border-red-500',
  };
  const borderColorClass = borderColors[lowerStatus] || 'border-zinc-700';

  // Status icon
  let statusIcon = null;
  if (lowerStatus === 'running') {
    statusIcon = (
      <div className="h-5 w-5 animate-spin rounded-full border-2 border-yellow-500 border-t-transparent" />
    );
  } else if (lowerStatus === 'success') {
    statusIcon = <IoMdCheckmark className="text-xl text-green-500" />;
  } else if (lowerStatus === 'failed') {
    statusIcon = <FaTimes className="text-xl text-red-500" />;
  }

  const depth = 10; // for 3D look

  return (
    <div
      className="relative h-full w-full"
      style={{ transformStyle: 'preserve-3d' }}
    >
      {/* Front Face */}
      <div
        className={`absolute inset-0 z-20 rounded-2xl border-2 bg-gradient-to-br from-zinc-800 to-zinc-700 ${borderColorClass} p-4 transition-all duration-200 ease-out
        ${
          isHovered
            ? 'shadow-[0_12px_20px_rgba(0,0,0,0.8),0_6px_10px_rgba(0,0,0,0.6)]'
            : 'shadow-[0_4px_6px_rgba(0,0,0,0.3),0_1px_3px_rgba(0,0,0,0.2)]'
        }`}
        style={{ transform: `translateZ(${depth}px)` }}
      >
        <div className="flex h-full flex-col justify-between gap-4">
          {/* Header: Workflow Title + Pulse Dot */}
          <div className="flex items-center justify-between">
            <p className="truncate text-xs font-semibold text-zinc-100">
              {workflowTitle}
            </p>
            <div
              className={`h-3 w-3 rounded-full ${borderColorClass} animate-pulse`}
            />
          </div>

          {/* Status Icon and Text */}
          <div className="mt-2 flex items-center gap-2">
            {statusIcon}
            <span className="text-xs font-medium capitalize text-zinc-400">
              {status}
            </span>
          </div>
        </div>
      </div>

      {/* 3D Faces */}
      <div
        className="absolute inset-x-0 top-0 z-10 rounded-t-2xl bg-zinc-600"
        style={{
          height: `${depth}px`,
          transform: `rotateX(90deg) translateZ(${depth / 2}px)`,
          transformOrigin: 'top',
        }}
      />
      <div
        className="absolute inset-y-0 right-0 z-10 rounded-tr-2xl bg-zinc-600"
        style={{
          width: `${depth}px`,
          transform: `rotateY(-90deg) translateZ(${depth / 2}px)`,
          transformOrigin: 'right',
        }}
      />
    </div>
  );
};
export default WorkflowExecutions;
